import { animate, useMotionValue, useReducedMotion, useTransform, motion } from "motion/react";
import { useEffect } from "react";
import { cn } from "@/lib/cn";
import { formatMoney } from "@/lib/format";
import { toCents } from "@/lib/money";
import type { Money as MoneyT } from "@/lib/types";

interface Props {
  value: MoneyT | number;
  /** Count up from 0 (or the previous value) when the value changes. */
  animated?: boolean;
  /** Show "+" and positive color for money coming in. */
  signed?: boolean;
  compact?: boolean;
  className?: string;
}

export function Money({ value, animated, signed, compact, className }: Props) {
  const cents = toCents(value);
  const reduce = useReducedMotion();
  const mv = useMotionValue(animated && !reduce ? 0 : cents);
  const text = useTransform(mv, (v) => formatMoney(Math.round(v) / 100, { compact }));

  useEffect(() => {
    if (!animated || reduce) {
      mv.set(cents);
      return;
    }
    const controls = animate(mv, cents, { duration: 0.9, ease: [0.16, 1, 0.3, 1] });
    return () => controls.stop();
  }, [cents, animated, reduce, mv]);

  const incoming = signed && cents < 0;
  const display = signed ? Math.abs(cents) : cents;
  const cls = cn("tabular whitespace-nowrap", incoming && "text-positive", className);

  if (!animated || reduce) {
    return (
      <span className={cls}>
        {incoming && "+"}
        {formatMoney(display / 100, { compact })}
      </span>
    );
  }
  return (
    <span className={cls}>
      {incoming && "+"}
      <motion.span>{text}</motion.span>
    </span>
  );
}
